import { ActionIcon, Avatar, Group, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import cx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMedia } from '@mygpt/app';

import ChatView, { FormValues } from '@/components/Widget/ChatView';
import { CHATBOT_ICON_ARRAY } from '@/components/Widget/Embed/Form/ChatbotIcon';
import { useWidgetFormContext } from '@/components/Widget/Embed/form-context';
import { SessionListParam } from '@/types/interface';

import classes from './index.module.css';

export default ({ viewChange }: { viewChange?: number }) => {
  const { t } = useTranslation();
  const demoForm = useForm<FormValues>({
    initialValues: {
      value: '',
    },
  });
  const { form, selectedNodes } = useWidgetFormContext();
  const [previewOpened, setPreviewOpened] = useState<boolean>(false);
  const { isMobile } = useMedia();

  const chatbotIconSizeRatio = useMemo(() => {
    const { iconButtonSize } = form.values.chatbotIcon;
    if (iconButtonSize && iconButtonSize.checked) {
      const sizeArr = [50, 58, 68];
      const Basic = 50;
      return sizeArr[iconButtonSize.value / Basic] / Basic;
    }

    return 1;
  }, [viewChange, form.values.chatbotIcon.iconButtonSize]);

  // 获取引导文案的辅助函数
  const getGreetingMessage = () => {
    const greetingMessageConfig = form.values.chatbot.suggestedMessages.greetingMessage;

    // 如果greetingMessage配置不存在，使用默认行为：显示默认文案
    if (!greetingMessageConfig) {
      return {
        shouldShow: true,
        greetingText: t('widget.setting.chatbot.suggested_greeting_message'),
      };
    }

    // 如果checked为false，不展示引导文案
    if (greetingMessageConfig.checked === false) {
      return {
        shouldShow: false,
        greetingText: '',
      };
    }

    // 如果checked为true（或未定义，默认为true）
    // 如果value为空或未定义，显示默认文案
    if (!greetingMessageConfig.value || greetingMessageConfig.value.trim() === '') {
      return {
        shouldShow: true,
        greetingText: t('widget.setting.chatbot.suggested_greeting_message'),
      };
    }

    // 如果value有内容，显示自定义文案
    return {
      shouldShow: true,
      greetingText: greetingMessageConfig.value,
    };
  };

  const welcomeMessage = useMemo(() => {
    const { checked, value } = form.values.chatbot.welcomeMessage;
    const suggestedMessagesConfig = form.values.chatbot.suggestedMessages;
    const chatbotMessage: SessionListParam[] = [];
    const userMessage: SessionListParam[] = [{ isAnswer: false, text: 'hi' }];

    // 处理欢迎语
    if (checked && !!value.trim()) {
      const customMessageArr = value.split(/[\n]/);
      customMessageArr.map((item) => {
        const message = item.trim();
        if (!message) return false;
        chatbotMessage.push({ isAnswer: true, text: message });
      });
    }

    // 处理推荐问题（与Web的非数字人模式保持一致）
    if (
      suggestedMessagesConfig.checked &&
      ((suggestedMessagesConfig.type === 'tree' &&
        (suggestedMessagesConfig.selectedNodeIds?.length || suggestedMessagesConfig.node_list.length)) ||
        (suggestedMessagesConfig.type !== 'tree' && suggestedMessagesConfig.value.trim()))
    ) {
      // 获取推荐问题数据
      let suggestedMessagesData: any[] = [];
      if (suggestedMessagesConfig.type === 'tree') {
        // 使用selectedNodes数据
        suggestedMessagesData = selectedNodes
          .filter((node) => node.question && node.question.trim())
          .map((node) => ({
            id: node.id,
            question: node.question,
          }));
      } else {
        // 文本类型
        suggestedMessagesData = suggestedMessagesConfig.value
          .split(/[\n]/)
          .map((item) => item.trim())
          .filter((item) => !!item);
      }

      if (suggestedMessagesData.length > 0) {
        // 获取引导文案
        const { shouldShow, greetingText } = getGreetingMessage();

        // 生成一个包含所有推荐问题的消息气泡（与Web的非数字人模式一致）
        chatbotMessage.push({
          isAnswer: true,
          text: shouldShow ? greetingText : '',
          suggestedMessages: suggestedMessagesData,
        });
      }
    }

    // 如果没有任何消息，添加默认消息用于预览颜色
    if (chatbotMessage.length === 0) {
      chatbotMessage.push({ isAnswer: true, text: 'Hi 👋 What can I help you with?' });
    }

    return chatbotMessage.concat(userMessage);
  }, [
    viewChange,
    form.values.chatbot.welcomeMessage,
    form.values.chatbot.suggestedMessages,
    form.values.chatbot.suggestedMessages.greetingMessage,
    selectedNodes,
  ]);

  const previewData = useMemo(() => {
    const data = {
      chatbotAvatar: form.values.chatbotAvatar,
    };
    const {
      answerSource,
      footerBrand,
      inputPlaceholder,
      securityNotice,
      signatureMessages,
      suggestedMessages,
      welcomeMessage,
      widgetColor,
      showBotAvatar,
    } = form.values.chatbot;

    // 添加 greetingMessage 配置
    if (suggestedMessages.greetingMessage) {
      Object.assign(data, { greetingMessage: suggestedMessages.greetingMessage });
    }

    if (welcomeMessage.checked) {
      const welcomeMessageArr: string[] = [];
      const customMessageArr = welcomeMessage.value.split(/[\n]/);
      customMessageArr.map((item) => {
        const message = item.trim();
        if (!message) return false;
        welcomeMessageArr.push(message);
      });
      Object.assign(data, { welcomeMessages: welcomeMessageArr });
    }
    if (suggestedMessages.checked) {
      if (suggestedMessages.type === 'tree') {
        // 直接使用selectedNodes数据（这是最准确和及时的数据源）
        if (selectedNodes.length > 0) {
          const treeMessages = selectedNodes
            .filter((node) => node.question && node.question.trim())
            .map((node) => ({
              id: node.id,
              question: node.question,
            }));

          Object.assign(data, { suggestedMessages: treeMessages });
        }
      } else {
        const suggestedMessageArr: string[] = [];
        const customMessageArr = suggestedMessages.value.split(/[\n]/);
        customMessageArr.map((item) => {
          const message = item.trim();
          if (!message) return false;
          suggestedMessageArr.push(message);
        });
        Object.assign(data, { suggestedMessages: suggestedMessageArr });
      }
    }

    if (inputPlaceholder.checked) {
      Object.assign(data, { inputPlaceholder: inputPlaceholder.value });
    }

    if (footerBrand.checked) {
      Object.assign(data, { footerBrand: footerBrand.value });
    }

    if (signatureMessages.checked) {
      Object.assign(data, {
        signatureMessages: {
          value: signatureMessages.value,
          list: signatureMessages.list,
        },
      });
    }

    const { botMessageColor, botMessageTextColor, chatColor, sendButtonColor, userMessageColor, userMessageTextColor } =
      widgetColor;
    const colorSettings = {
      chatColor: '',
      sendButtonColor: '',
      userMessageColor: '',
      userMessageTextColor: '',
      botMessageColor: '',
      botMessageTextColor: '',
    };
    if (chatColor.checked) Object.assign(colorSettings, { chatColor: chatColor.value });
    if (sendButtonColor.checked) Object.assign(colorSettings, { sendButtonColor: sendButtonColor.value });
    if (userMessageColor.checked) Object.assign(colorSettings, { userMessageColor: userMessageColor.value });
    if (userMessageTextColor.checked)
      Object.assign(colorSettings, { userMessageTextColor: userMessageTextColor.value });
    if (botMessageColor.checked) Object.assign(colorSettings, { botMessageColor: botMessageColor.value });
    if (botMessageTextColor.checked) Object.assign(colorSettings, { botMessageTextColor: botMessageTextColor.value });

    // 添加背景图片配置
    const { chatHeaderImage } = form.values.chatbot;
    const backgroundImageSettings = {
      chatHeaderImage: chatHeaderImage.checked && chatHeaderImage.url ? chatHeaderImage.url : '',
    };

    Object.assign(data, {
      widgetColor: colorSettings,
      answerSource,
      showBotAvatar,
      securityNotice,
      ...backgroundImageSettings,
    });

    return data;
  }, [
    viewChange,
    form.values.chatbot.widgetColor,
    form.values.chatbot.suggestedMessages,
    form.values.chatbot.chatHeaderImage,
    form.values.chatbot.securityNotice,
    selectedNodes,
    form.values.chatbot.suggestedMessages.selectedNodeIds,
  ]);

  const onChange = () => {
    // console.log('preview onchange...', form.values);
  };

  const getChatbotSystemIcon = (index: number) => {
    const Icon = CHATBOT_ICON_ARRAY[index];
    const size = 24 * chatbotIconSizeRatio;
    return <Icon style={{ width: size, height: size }} />;
  };

  useEffect(() => {
    onChange();
  }, [viewChange]);

  useEffect(() => {
    setPreviewOpened(false);
  }, [isMobile]);

  const togglePreviewOpened = () => {
    setPreviewOpened(!previewOpened);
  };

  const iconPosition = useMemo(() => {
    return form.values.chatbotIcon.position.checked ? form.values.chatbotIcon.position.value : 'right';
  }, [form.values.chatbotIcon.position.value, form.values.chatbotIcon.position.checked]);

  return (
    <section className="w-full flex flex-col justify-center px-12 py-6">
      <Text fz={24}>{t('widget.preview')}</Text>
      <Group
        justify={iconPosition === 'right' ? 'flex-end' : 'flex-start'}
        mt={20}
        className={cx(
          'bottom-5 w-[300px] lg:w-[390px]',
          iconPosition === 'left' ? 'right-[85%] sm:right-[400px]' : 'right-5'
        )}
      >
        <section
          className={cx(
            'h-[30rem] lg:h-[38rem] w-full overflow-auto rounded-xl bg-white',
            // !previewShow && 'hidden',
            iconPosition === 'left' ? '-right-[300px] sm:-right-[395px]' : '-left-[300px] sm:-left-[395px]'
          )}
        >
          <ChatView
            title={
              form.values.chatbot.chatHeaderDisplay.checked ? form.values.chatbot.chatHeaderDisplay.value : 'Title'
            }
            onClose={() => {}}
            onRefresh={() => {}}
            loading={false}
            sessionList={{
              messages: welcomeMessage,
            }}
            submit={() => {}}
            form={demoForm}
            closeAutoFocus={true}
            configs={previewData}
          />
        </section>

        <ActionIcon
          className={classes.chatIcon}
          variant={'filled'}
          size={50 * chatbotIconSizeRatio}
          bg={form.values.chatbotIcon.color.checked ? form.values.chatbotIcon.color.value : ''}
          onClick={togglePreviewOpened}
          style={{ borderRadius: 25 * chatbotIconSizeRatio }}
        >
          {form.values.chatbotIcon.icon.type === 'system' ? (
            getChatbotSystemIcon(form.values.chatbotIcon.icon.index || 0)
          ) : (
            <Avatar
              size={Math.ceil(38 * chatbotIconSizeRatio)}
              src={form.values.chatbotIcon.icon.url}
              radius="xl"
              onClick={togglePreviewOpened}
            />
          )}

          {form.values.chatbotIcon.iconButtonText.checked && form.values.chatbotIcon.iconButtonText.value ? (
            <Text style={{ fontSize: 14 * chatbotIconSizeRatio }} ml={5}>
              {form.values.chatbotIcon.iconButtonText.value}
            </Text>
          ) : (
            <></>
          )}
        </ActionIcon>
      </Group>
    </section>
  );
};
